import React, { useState } from 'react';
import { Button } from './ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { useToast } from '../hooks/use-toast';
import { FolderArchive, Loader2 } from 'lucide-react';
import fileService from '../services/fileService';

/**
 * BulkDownloadProductAttachmentsButton component
 * Downloads all product attachments from a SO/PO/Quotation as a single ZIP file
 *
 * @param {Object} props
 * @param {string} props.entityType - Entity type ('sales_orders', 'quotations', 'purchases')
 * @param {string} props.entityId - Entity MongoDB _id
 * @param {string} props.entityName - Entity display name (e.g., "SO-001") for filename
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {string} props.variant - Button variant (default: 'outline')
 * @param {string} props.size - Button size (default: 'default')
 */
const BulkDownloadProductAttachmentsButton = ({
  entityType,
  entityId,
  entityName,
  disabled = false,
  variant = 'outline',
  size = 'default',
}) => {
  const [downloading, setDownloading] = useState(false);
  const { toast } = useToast();

  const handleDownload = async () => {
    try {
      setDownloading(true);

      await fileService.bulkDownloadEntityProductAttachments(
        entityType,
        entityId,
        entityName
      );

      toast({
        title: '下载成功',
        description: '产品附件下载成功！',
      });
    } catch (err) {
      console.error('Bulk download error:', err);

      let errorMessage = '下载失败';

      // Handle specific error cases
      if (err.response?.status === 404) {
        // Handle "no attachments found" case with a user-friendly message
        if (err.response?.data?.error?.toLowerCase().includes('no attachments')) {
          errorMessage = '没有找到可下载的产品附件。请先为产品上传附件文件。';
        } else {
          errorMessage = '未找到相关附件文件';
        }
      } else if (err.response?.data?.error) {
        // Translate common backend error messages to Chinese
        const backendError = err.response.data.error;
        if (backendError.toLowerCase().includes('no attachments found')) {
          errorMessage = '没有找到可下载的产品附件。请先为产品上传附件文件。';
        } else if (backendError.toLowerCase().includes('entity not found')) {
          errorMessage = '未找到相关记录';
        } else {
          errorMessage = backendError;
        }
      } else if (err.message) {
        // Handle Axios default error messages
        if (err.message === 'Request failed with status code 404') {
          errorMessage = '没有找到可下载的产品附件。请先为产品上传附件文件。';
        } else if (err.message.includes('Network Error')) {
          errorMessage = '网络连接错误，请检查网络后重试';
        } else {
          errorMessage = err.message;
        }
      }

      toast({
        variant: 'destructive',
        title: '下载失败',
        description: errorMessage,
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Button
              variant={variant}
              size={size}
              onClick={handleDownload}
              disabled={disabled || downloading}
              className="font-semibold"
            >
              {downloading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FolderArchive className="mr-2 h-4 w-4" />
              )}
              {downloading ? '下载中...' : '下载全部产品附件'}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>将此单据中所有产品的附件打包下载为ZIP压缩文件</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default BulkDownloadProductAttachmentsButton;
